import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIES = [
  { value: "nieuw", label: "Nieuw", cls: "bg-[#E6AF68]/20 text-[#9C6D3B] border-[#E6AF68]" },
  { value: "in_behandeling", label: "In behandeling", cls: "bg-[#2B231D]/5 text-[#2B231D] border-[#B0A396]" },
  { value: "afgerond", label: "Afgerond", cls: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/40" },
];

export const AanvragenList = () => {
  const [aanvragen, setAanvragen] = useState(null);

  const laad = () =>
    axios
      .get(`${API}/aanvragen`, { withCredentials: true })
      .then((res) => setAanvragen(res.data))
      .catch(() => setAanvragen([]));

  useEffect(() => {
    laad();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/aanvragen/${id}/status`, { status }, { withCredentials: true });
      setAanvragen((lijst) => lijst.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Status bijgewerkt");
    } catch (err) {
      toast.error("Status wijzigen mislukt");
    }
  };

  if (aanvragen === null) {
    return <p className="text-sm text-[#8C7A6B]">Aanvragen laden...</p>;
  }

  if (aanvragen.length === 0) {
    return (
      <div data-testid="aanvragen-empty" className="bg-white rounded-3xl border border-[#EAE3DA] p-10 text-center">
        <p className="text-[#6C5E52] text-sm">Nog geen aanvragen ontvangen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aanvragen-list">
      {aanvragen.map((a) => {
        const huidig = STATUS_OPTIES.find((s) => s.value === (a.status || "nieuw"));
        return (
          <div key={a.id} data-testid={`aanvraag-item-${a.id}`} className="bg-white rounded-2xl border border-[#EAE3DA] p-6">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <span className="block text-base font-bold text-[#2B231D] font-display">{a.naam}</span>
                <span className="block text-xs text-[#8C7A6B]">
                  {new Date(a.created_at).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                </span>
              </div>
              <span className="px-3 py-1 rounded-full bg-[#F4EFEA] text-[#9C6D3B] text-xs font-semibold border border-[#EAE3DA]">
                {a.type_meubel}
              </span>
            </div>
            <div className="text-sm text-[#594E44] space-y-1">
              <p>
                <a href={`mailto:${a.email}`} className="text-[#9C6D3B] font-medium hover:underline" data-testid={`aanvraag-email-${a.id}`}>{a.email}</a>
                {a.telefoon && <span className="text-[#8C7A6B]"> · {a.telefoon}</span>}
              </p>
              {a.omschrijving && <p className="leading-relaxed">{a.omschrijving}</p>}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-[#F0EBE3]">
              <div className="flex flex-wrap items-center gap-2" data-testid={`aanvraag-status-${a.id}`}>
                {STATUS_OPTIES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`status-${a.id}-${s.value}`}
                    onClick={() => setStatus(a.id, s.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${s.cls} ${
                      huidig?.value === s.value ? "ring-2 ring-offset-1 ring-[#9C6D3B]/40" : "opacity-50 hover:opacity-100"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {a.schets_path && (
                <a
                  href={`${API}/files/${a.schets_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`aanvraag-schets-${a.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2B231D] text-[#F9F6F0] text-xs font-medium hover:bg-[#43352B] transition-colors"
                >
                  <Paperclip size={13} />
                  Bekijk schets{a.schets_naam ? `: ${a.schets_naam}` : ""}
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
